import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

import st1 from "../../assets/images/testimonial1.JPG";
import st2 from "../../assets/images/testimonial2.JPG";
import st3 from "../../assets/images/st3.png";
import st4 from "../../assets/images/st4.png";

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      name: "Raju Ranjan",
      image: st1,
      role: "CNC Operator",
      rating: 5,
      testimonial: "I always wanted to work with machines, and the CNC operator course at Skillserve gave me the skills I needed. The trainers were really helpful, and the hands-on experience was great. I feel more confident now and have already started working in a new job!"
    },
    {
      id: 2,
      name: "Mohit Yadav",
      image: st2,
      role: "CNC + VMC Operator",
      rating: 5,
      testimonial: "The CNC course at Skillserve was exactly what I needed. I had no prior experience, but the course was easy to follow and very practical. The best part was learning how to operate real CNC machines. I’m now working at a local manufacturing company"
    },
    {
      id: 3,
      name: "Pooja",
      image: st3,
      role: "Digital Marketing Specialist",
      rating: 5,
      testimonial: "I'm thrilled with my experience at Skill Serve! The digital marketing course was comprehensive, engaging, and up-to-date with the latest trends. The platform made it easy to access everything I needed, anytime, anywhere."
    },
    // {
    //   id: 4,
    //   name: "",
    //   image: st4,
    //   role: "Data Scientist",
    //   rating: 5,
    //   testimonial: "Skill Serve's data science course exceeded my expectations. The content is practical, the instructors are experts, and the support is incredible. It has given me the confidence to pursue a career in this high-demand field!"
    // }
  ];

  const cardsPerView = isDesktop ? 3 : isMobile ? 1 : 2;
  const maxIndex = Math.max(0, testimonials.length - cardsPerView);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  // Auto-scroll
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [maxIndex]);

  const wrapStyle = {
    width: '100%',
    padding: '80px 0',
    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f0f4f8 100%)',
    position: 'relative',
    overflow: 'hidden',
  };

  const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 24px',
    position: 'relative',
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px',
  };

  const eyebrowStyle = {
    color: '#0b3161',
    fontWeight: 700,
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '12px',
  };

  const headingStyle = {
    fontFamily: "'Roboto Slab', serif",
    color: '#081426',
    fontSize: isDesktop ? '2.8rem' : isMobile ? '2rem' : '2.4rem',
    margin: '0 0 16px 0',
    lineHeight: 1.1,
    fontWeight: 700,
  };

  const descStyle = {
    color: '#64748b',
    fontSize: '1.1rem',
    margin: '0 auto 32px',
    lineHeight: 1.7,
    maxWidth: '720px',
  };

  const ratingBoxStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '16px',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    padding: '20px 32px',
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(11, 49, 97, 0.08)',
    border: '1px solid rgba(11, 49, 97, 0.06)',
  };

  const ratingScoreStyle = {
    fontSize: '3rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #fdd649 0%, #f7c42d 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1,
  };

  const sliderContainerStyle = {
    position: 'relative',
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const sliderWrapperStyle = {
    overflow: 'hidden',
    padding: '0 60px',
  };

  const sliderTrackStyle = {
    display: 'flex',
    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: `translateX(-${currentIndex * (100 / cardsPerView)}%)`,
  };

  const slideStyle = {
    flex: `0 0 ${100 / cardsPerView}%`,
    padding: '0 12px',
  };

  const cardStyle = (hovered) => ({
    background: '#ffffff',
    borderRadius: '20px',
    padding: '32px',
    boxShadow: hovered 
      ? '0 24px 48px rgba(11, 49, 97, 0.15)' 
      : '0 12px 32px rgba(11, 49, 97, 0.08)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: hovered ? 'translateY(-8px)' : 'translateY(0)',
    border: '1px solid rgba(11, 49, 97, 0.06)',
    position: 'relative',
    minHeight: '420px',
    display: 'flex',
    flexDirection: 'column',
  });

  const quoteIconStyle = {
    position: 'absolute',
    top: '24px',
    right: '24px',
    opacity: 0.1,
  };

  const avatarWrapperStyle = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
    position: 'relative',
  };

  const avatarStyle = {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #fdd649',
    boxShadow: '0 8px 24px rgba(253, 214, 73, 0.3)',
  };

  const badgeStyle = {
    position: 'absolute',
    bottom: '-8px',
    right: 'calc(50% - 60px)',
    background: 'linear-gradient(135deg, #0b3161 0%, #1e4a7a 100%)',
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '999px',
    fontSize: '0.7rem',
    fontWeight: 700,
    boxShadow: '0 4px 12px rgba(11, 49, 97, 0.3)',
  };

  const testimonialTextStyle = {
    color: '#475569',
    fontSize: '0.95rem',
    lineHeight: 1.8,
    marginBottom: '24px',
    flex: 1,
    fontStyle: 'italic',
    display: '-webkit-box',
    WebkitLineClamp: 6,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    minHeight: '140px',
  };

  const authorStyle = {
    marginTop: 'auto',
    paddingTop: '20px',
    borderTop: '1px solid rgba(11, 49, 97, 0.06)',
  };

  const nameStyle = {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#0b3161',
    margin: '0 0 4px 0',
  };

  const roleStyle = {
    fontSize: '0.9rem',
    color: '#64748b',
    marginBottom: '12px',
  };

  const starsStyle = {
    display: 'flex',
    gap: '4px',
  };

  const navButtonStyle = (disabled) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: 'none',
    background: disabled 
      ? 'rgba(11, 49, 97, 0.1)' 
      : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    boxShadow: '0 8px 24px rgba(11, 49, 97, 0.12)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    opacity: disabled ? 0.5 : 1,
    zIndex: 10,
  });

  const dotsStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '40px',
  };

  const dotStyle = (active) => ({
    width: active ? '32px' : '10px',
    height: '10px',
    borderRadius: '5px',
    background: active 
      ? 'linear-gradient(90deg, #fdd649, #f7c42d)' 
      : 'rgba(11, 49, 97, 0.2)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: active ? '0 4px 12px rgba(253, 214, 73, 0.3)' : 'none',
  });

  return (
    <section style={wrapStyle}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={eyebrowStyle}>Learner Success Stories</div>
          <h2 style={headingStyle}>What Our Learners Say</h2>
          <p style={descStyle}>
            Join thousands of successful professionals who have transformed their careers through our industry-focused training programs.
          </p>
          
          <div style={ratingBoxStyle}>
            <div style={ratingScoreStyle}>4.8</div>
            <div>
              <div style={starsStyle}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} fill="#fdd649" color="#fdd649" />
                ))}
              </div>
              <div style={{ color: '#0b3161', fontWeight: 600, fontSize: '0.95rem', marginTop: '4px' }}>
                8,154 Reviews
              </div>
            </div>
          </div>
        </div>

        {/* Slider */}
        <div style={sliderContainerStyle}>
          <button
            style={{ ...navButtonStyle(currentIndex === 0), left: '0' }}
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <ChevronLeft size={24} color={currentIndex === 0 ? '#94a3b8' : '#0b3161'} />
          </button>

          <button
            style={{ ...navButtonStyle(currentIndex >= maxIndex), right: '0' }}
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
          >
            <ChevronRight size={24} color={currentIndex >= maxIndex ? '#94a3b8' : '#0b3161'} />
          </button>

          <div style={sliderWrapperStyle}>
            <div style={sliderTrackStyle}>
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} style={slideStyle}>
                  <div
                    style={cardStyle(hoveredId === testimonial.id)}
                    onMouseEnter={() => setHoveredId(testimonial.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div style={quoteIconStyle}>
                      <Quote size={80} color="#0b3161" />
                    </div>

                    <div style={avatarWrapperStyle}>
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        style={avatarStyle}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/100/0b3161/ffffff?text=' + testimonial.name[0];
                        }}
                      />
                      <div style={badgeStyle}>Verified</div>
                    </div>

                    <p style={testimonialTextStyle}>
                      "{testimonial.testimonial}"
                    </p>

                    <div style={authorStyle}>
                      <h4 style={nameStyle}>{testimonial.name}</h4>
                      <p style={roleStyle}>{testimonial.role}</p>
                      <div style={starsStyle}>
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} size={16} fill="#fdd649" color="#fdd649" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dots */}
        <div style={dotsStyle}>
          {[...Array(maxIndex + 1)].map((_, index) => (
            <div
              key={index}
              style={dotStyle(currentIndex === index)}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}